import type { RootState } from '@/store';
import { Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_QUALIFY_STATUS } from '@/config/constants';
import { useGetOrCreatePurchaseLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import type { Product } from '@/types/ProductType';

type Props = {
  productId: string;
  product?: Pick<Product, 'transactionMode'>;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
};

export function ContinueMortgageFileButton({
  productId,
  product,
  variant = 'outlined',
  size = 'small',
}: Props) {
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const isInvestorMode = useSelector((s: RootState) => s.mode.isInvestorMode);
  const [getOrCreate, { isLoading }] =
    useGetOrCreatePurchaseLoanApplicationMutation();

  if (!user || user.qualifyStatus !== USER_QUALIFY_STATUS.QUALIFY) {
    return null;
  }
  if (product?.transactionMode === 'refinance') {
    return null;
  }

  const onClick = async () => {
    try {
      const app = await getOrCreate({
        flowProfile: isInvestorMode ? 'investor_dscr' : 'purchase',
        focusProductId: productId,
        approvalChannel: 'property_detail',
        investorModeAtCreation: isInvestorMode,
      }).unwrap();
      navigate(
        `/loan/${app._id}?focus=${encodeURIComponent(
          productId
        )}&section=property`
      );
    } catch {
      toast.error('Could not open your mortgage file.');
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      color="inherit"
      disabled={isLoading}
      onClick={() => void onClick()}
      startIcon={<i className="fa-light fa-file-lines" />}
      sx={{ mt: 1, color: 'inherit' }}
    >
      Continue mortgage file
    </Button>
  );
}
